import { useMemo } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import Breadcrumb from "@/components/base/Breadcrumb";
import DramaCard from "@/components/base/DramaCard";
import Pagination from "@/components/base/Pagination";
import FilterBar, { type Filters } from "./components/FilterBar";
import { dramas } from "@/mocks/dramas";
import { categories } from "@/mocks/categories";
import { useSeo } from "@/hooks/useSeo";

const PAGE_SIZE = 8;

export default function CategoryPage() {
  const { categoryName = "tv" } = useParams();
  const [searchParams, setSearchParams] = useSearchParams();

  const meta = categories.find((c) => c.key === categoryName);
  const categoryLabel = meta?.name ?? "影视大全";
  const categoryDesc =
    meta?.desc ?? "汇集赏金女王影视大全全部影视作品，支持多维度筛选与排序，快速找到你想看的内容。";

  useSeo({
    title: `${categoryLabel} - 赏金女王影视大全`,
    description: `${categoryLabel}分类：${categoryDesc}`,
    keywords: `${categoryLabel},赏金女王,影视剧,电视剧,影视资讯`,
  });

  const filters: Filters = {
    region: searchParams.get("region") ?? "全部",
    year: searchParams.get("year") ?? "全部",
    genre: searchParams.get("genre") ?? "全部",
  };
  const sort = searchParams.get("sort") ?? "latest";
  const page = Number(searchParams.get("page") ?? "1") || 1;

  const updateParams = (next: Record<string, string>, resetPage = true) => {
    const params = new URLSearchParams(searchParams);
    Object.entries(next).forEach(([k, v]) => {
      if (!v || v === "全部") params.delete(k);
      else params.set(k, v);
    });
    if (resetPage) params.delete("page");
    setSearchParams(params);
  };

  const handleReset = () => {
    const params = new URLSearchParams();
    if (sort !== "latest") params.set("sort", sort);
    setSearchParams(params);
  };

  const filtered = useMemo(() => {
    let list = dramas.filter((d) => {
      if (categoryName === "tv") return d.category === "tv";
      if (categoryName === "movie") return d.category === "movie";
      if (categoryName === "classic") return d.isClassic;
      return true;
    });
    if (filters.region !== "全部") list = list.filter((d) => d.region === filters.region);
    if (filters.year !== "全部") list = list.filter((d) => String(d.year) === filters.year);
    if (filters.genre !== "全部") list = list.filter((d) => d.genres.includes(filters.genre));

    const sorted = [...list];
    if (sort === "hot") sorted.sort((a, b) => b.popularity - a.popularity);
    else if (sort === "rating") sorted.sort((a, b) => b.rating - a.rating);
    else sorted.sort((a, b) => b.updateTime.localeCompare(a.updateTime));
    return sorted;
  }, [categoryName, filters.region, filters.year, filters.genre, sort]);

  const totalPages = Math.ceil(filtered.length / PAGE_SIZE);
  const safePage = Math.min(page, Math.max(totalPages, 1));
  const pageItems = filtered.slice((safePage - 1) * PAGE_SIZE, safePage * PAGE_SIZE);

  return (
    <div className="w-full px-4 md:px-8 py-6 md:py-10">
      <div className="mb-6">
        <Breadcrumb items={[{ name: "首页", path: "/" }, { name: "分类" }, { name: categoryLabel }]} />
      </div>

      <header className="mb-6 md:mb-8">
        <h1 className="font-heading text-2xl md:text-4xl font-bold text-foreground-950">
          {categoryLabel}
        </h1>
        <p className="mt-3 text-sm md:text-base text-foreground-600 max-w-3xl leading-relaxed">
          {categoryDesc}
        </p>
      </header>

      <FilterBar
        filters={filters}
        onChange={(f) => updateParams({ region: f.region, year: f.year, genre: f.genre })}
        sort={sort}
        onSortChange={(s) => updateParams({ sort: s })}
        onReset={handleReset}
      />

      <div className="mt-6 flex items-center justify-between text-sm text-foreground-500">
        <span>
          共找到 <strong className="text-accent-600">{filtered.length}</strong> 部作品
        </span>
      </div>

      {pageItems.length > 0 ? (
        <>
          <div className="mt-4 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
            {pageItems.map((drama) => (
              <DramaCard key={drama.id} drama={drama} />
            ))}
          </div>
          <div className="mt-10">
            <Pagination
              page={safePage}
              totalPages={totalPages}
              onChange={(p) => updateParams({ page: String(p) }, false)}
            />
          </div>
        </>
      ) : (
        <div className="mt-10 rounded-lg border border-dashed border-background-300 bg-background-50 py-16 text-center">
          <span className="w-14 h-14 mx-auto rounded-full bg-background-100 text-foreground-400 flex items-center justify-center text-2xl mb-4">
            <i className="ri-emotion-sad-line" aria-hidden="true"></i>
          </span>
          <p className="text-foreground-700 font-medium">没有找到符合当前筛选条件的作品</p>
          <p className="mt-1 text-sm text-foreground-500">试试调整筛选条件，或重置后重新浏览</p>
          <div className="mt-6 flex flex-wrap items-center justify-center gap-3">
            <button
              type="button"
              onClick={handleReset}
              className="px-5 py-2.5 rounded-md bg-accent-500 text-primary-950 text-sm font-medium hover:bg-accent-600 transition-colors whitespace-nowrap cursor-pointer"
            >
              重置筛选
            </button>
            <Link
              to="/"
              className="px-5 py-2.5 rounded-md border border-background-300 text-sm text-foreground-700 hover:bg-background-100 transition-colors whitespace-nowrap cursor-pointer"
            >
              返回首页
            </Link>
          </div>
        </div>
      )}
    </div>
  );
}